import React, { useContext } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Avatar,
  Box,
  ButtonBase,
  Drawer,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import AuthContext from '../../context/AuthContext';
import { INK, MUTED, RULE } from '../analytics/analyticsUi';

const DRAWER_WIDTH = 240;
const COLLAPSED_WIDTH = 64;
const NAVBAR_HEIGHT = 72;

const ACTIVE_BG = 'rgba(37, 211, 102, 0.12)';
const ACTIVE_TEXT = '#0D7332';

const MAIN_ITEMS = [
  { label: 'Dashboard', icon: SpaceDashboardOutlinedIcon, path: '/dashboard' },
  {
    label: 'WhatsApp accounts',
    icon: WhatsAppIcon,
    path: '/whatsapp-accounts',
    // Account analytics and onboarding belong to this section
    match: ['/whatsapp-accounts', '/whatsapp-analytics', '/whatsapp-onboarding']
  },
  { label: 'Templates', icon: ChatBubbleOutlineIcon, path: '/templates' },
  { label: 'Billing', icon: ReceiptLongOutlinedIcon, path: '/billing' },
  { label: 'Profile', icon: PersonOutlineIcon, path: '/profile' }
];

const ADMIN_ITEMS = [
  { label: 'Users', icon: GroupOutlinedIcon, path: '/admin/users' },
  { label: 'Subscriptions', icon: AutorenewOutlinedIcon, path: '/admin/subscriptions' },
  { label: 'Payments', icon: AccountBalanceWalletOutlinedIcon, path: '/admin/payments' },
  { label: 'Invoices', icon: RequestQuoteOutlinedIcon, path: '/admin/invoices' },
  { label: 'Plans', icon: SellOutlinedIcon, path: '/admin/plans' },
  { label: 'Message rates', icon: PaidOutlinedIcon, path: '/admin/rates' },
  { label: 'Servers', icon: DnsOutlinedIcon, path: '/admin/servers' },
  { label: 'Database backups', icon: StorageOutlinedIcon, path: '/admin/database' },
  { label: 'Audit log', icon: HistoryOutlinedIcon, path: '/admin/audit' }
];

const isItemActive = (item, pathname) =>
  (item.match || [item.path]).some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

// A single navigation row; shows a tooltip with the label when the menu is collapsed
const NavItem = ({ item, active, collapsed, onNavigate, external }) => {
  const Icon = item.icon;
  const linkProps = external
    ? { component: 'a', href: item.href, target: '_blank', rel: 'noopener noreferrer' }
    : { component: RouterLink, to: item.path };

  const row = (
    <ButtonBase
      {...linkProps}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? item.label : undefined}
      sx={{
        width: '100%',
        height: 40,
        px: collapsed ? 0 : 1.5,
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: 1.5,
        borderRadius: 1.5,
        color: active ? ACTIVE_TEXT : INK,
        bgcolor: active ? ACTIVE_BG : 'transparent',
        fontFamily: 'inherit',
        transition: 'background-color 120ms, color 120ms',
        '&:hover': { bgcolor: active ? ACTIVE_BG : 'rgba(31, 42, 61, 0.05)' },
        '&.Mui-focusVisible': { outline: `2px solid ${ACTIVE_TEXT}`, outlineOffset: -2 }
      }}
    >
      <Icon sx={{ fontSize: 20, color: active ? ACTIVE_TEXT : MUTED, flexShrink: 0 }} />
      {!collapsed && (
        <Typography
          component="span"
          noWrap
          sx={{ fontSize: '0.9rem', fontWeight: active ? 600 : 500, color: 'inherit', lineHeight: 1 }}
        >
          {item.label}
        </Typography>
      )}
    </ButtonBase>
  );

  return (
    <Box component="li" sx={{ mb: 0.25 }}>
      {collapsed ? (
        <Tooltip title={item.label} placement="right" arrow>
          {row}
        </Tooltip>
      ) : row}
    </Box>
  );
};

const NavGroup = ({ label, items, pathname, collapsed, onNavigate }) => (
  <Box component="nav" aria-label={label} sx={{ mb: 1 }}>
    {label && (collapsed ? (
      <Box sx={{ height: 1, bgcolor: RULE, mx: 1.5, my: 1.5 }} />
    ) : (
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: MUTED, px: 1.5, pt: 2, pb: 0.75 }}>
        {label}
      </Typography>
    ))}
    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
      {items.map((item) => (
        <NavItem
          key={item.path}
          item={item}
          active={isItemActive(item, pathname)}
          collapsed={collapsed}
          onNavigate={onNavigate}
        />
      ))}
    </Box>
  </Box>
);

const SidebarContent = ({ collapsed, onNavigate, onToggleCollapse, showCollapse }) => {
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const initial = (user?.name || user?.email || '?').charAt(0).toUpperCase();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', px: collapsed ? 1 : 1.5, py: 2 }}>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <NavGroup label={null} items={MAIN_ITEMS} pathname={location.pathname} collapsed={collapsed} onNavigate={onNavigate} />
        {isAdmin && (
          <NavGroup label="Admin" items={ADMIN_ITEMS} pathname={location.pathname} collapsed={collapsed} onNavigate={onNavigate} />
        )}
      </Box>

      <Box sx={{ borderTop: `1px solid ${RULE}`, pt: 1.5 }}>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          <NavItem
            item={{ label: 'WhatsApp API docs', icon: HelpOutlineIcon, href: 'https://developers.facebook.com/docs/whatsapp' }}
            collapsed={collapsed}
            external
          />
        </Box>

        {user && (
          <Box
            component={RouterLink}
            to="/profile"
            onClick={onNavigate}
            aria-label={collapsed ? `Profile for ${user.name}` : undefined}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 1.25,
              mt: 1,
              p: collapsed ? 0.5 : 1,
              borderRadius: 1.5,
              textDecoration: 'none',
              '&:hover': { bgcolor: 'rgba(31, 42, 61, 0.05)' },
              '&:focus-visible': { outline: `2px solid ${ACTIVE_TEXT}`, outlineOffset: -2 }
            }}
          >
            <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', fontWeight: 600, bgcolor: '#128C7E' }}>
              {initial}
            </Avatar>
            {!collapsed && (
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 600, color: INK, lineHeight: 1.3 }}>
                  {user.name}
                </Typography>
                <Typography noWrap sx={{ fontSize: '0.75rem', color: MUTED, lineHeight: 1.3 }}>
                  {isAdmin ? 'Administrator' : user.email}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {showCollapse && (
          <Tooltip title={collapsed ? 'Expand menu' : ''} placement="right" arrow>
            <ButtonBase
              onClick={onToggleCollapse}
              aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
              sx={{
                mt: 1,
                width: '100%',
                height: 36,
                borderRadius: 1.5,
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: 1.5,
                px: collapsed ? 0 : 1.5,
                color: MUTED,
                fontFamily: 'inherit',
                '&:hover': { bgcolor: 'rgba(31, 42, 61, 0.05)', color: INK },
                '&.Mui-focusVisible': { outline: `2px solid ${ACTIVE_TEXT}`, outlineOffset: -2 }
              }}
            >
              {collapsed
                ? <KeyboardDoubleArrowRightIcon sx={{ fontSize: 20 }} />
                : <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 20 }} />}
              {!collapsed && <Typography component="span" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>Collapse</Typography>}
            </ButtonBase>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const Sidebar = ({ mobileOpen, handleDrawerToggle, collapsed, onToggleCollapse }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const paperBase = {
    boxSizing: 'border-box',
    top: NAVBAR_HEIGHT,
    height: `calc(100% - ${NAVBAR_HEIGHT}px)`,
    bgcolor: '#FFFFFF',
    borderRight: `1px solid ${RULE}`,
    boxShadow: 'none',
    overflowX: 'hidden'
  };

  return (
    <>
      {/* Mobile: slide-in drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { ...paperBase, width: DRAWER_WIDTH }
        }}
      >
        <SidebarContent collapsed={false} onNavigate={isMobile ? handleDrawerToggle : undefined} />
      </Drawer>

      {/* Tablet and desktop: fixed menu that can collapse to icons */}
      <Drawer
        variant="permanent"
        aria-label="Main menu"
        sx={{
          display: { xs: 'none', sm: 'block' },
          // Out of the page flow: AppLayout offsets the content with a matching margin
          position: 'fixed',
          width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            ...paperBase,
            position: 'fixed',
            zIndex: 1100,
            width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
            transition: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1)',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
          }
        }}
      >
        <SidebarContent collapsed={collapsed} onToggleCollapse={onToggleCollapse} showCollapse />
      </Drawer>
    </>
  );
};

export default Sidebar;
